import React, { useEffect, useState } from "react";
import allImages from "../../../assets/images-import";
import { Link, useLocation } from "react-router-dom";
import { getMenus } from "../../../services/manageAccess";
import { handleErrors } from "../../../utils/errorHandler";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

const toPagePath = (pageLink) => {
  const safe = (pageLink || "").trim().replace(/^\/+/, "");
  return safe ? `/${safe}` : "/";
};

const toCollapseId = (groupName) =>
  `sidebar${String(groupName || "").replace(/\s+/g, "")}`;

const isSidebarCollapsed = () =>
  document.documentElement.getAttribute("data-sidebar-size") === "sm";

const expandSidebar = () => {
  document.documentElement.setAttribute("data-sidebar-size", "lg");
};

export const Navbar = () => {
  const [menus, setMenus] = useState([]);
  const [openGroup, setOpenGroup] = useState("");
  const location = useLocation();

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isGroupActive = (pages) =>
    pages.some((page) => isActive(toPagePath(page.pageLink)));

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const response = await getMenus();
        const filteredMenus = response.data.result.filter(
          (menu) => menu.showInMenu
        );
        setMenus(filteredMenus);
      } catch (error) {
        handleErrors(error);
      }
    };
    fetchMenus();
  }, []);

  // Keep active group open when route changes
  useEffect(() => {
    const groups = menus.reduce((acc, menu) => {
      if (!menu.showInMenu) return acc;
      if (!acc[menu.pageGroupName]) acc[menu.pageGroupName] = [];
      acc[menu.pageGroupName].push(menu);
      return acc;
    }, {});

    const active = Object.keys(groups).find((name) =>
      groups[name].some((page) => isActive(toPagePath(page.pageLink)))
    );
    if (active) setOpenGroup(toCollapseId(active));

    // Close mobile sidebar after navigation
    document.body.classList.remove("vertical-sidebar-enable");
  }, [location.pathname, menus]);

  const groupedMenus = menus.reduce((acc, menu) => {
    if (!acc[menu.pageGroupName]) {
      acc[menu.pageGroupName] = [];
    }
    acc[menu.pageGroupName].push(menu);
    return acc;
  }, {});

  const handleGroupToggle = (e, collapseId) => {
    e.preventDefault();
    e.stopPropagation();

    // Collapsed icon mode → expand sidebar, then open this group
    if (isSidebarCollapsed() && !document.body.classList.contains("vertical-sidebar-enable")) {
      expandSidebar();
      setOpenGroup(collapseId);
      return;
    }

    // Accordion: open one group at a time; click again to close
    setOpenGroup((prev) => (prev === collapseId ? "" : collapseId));
  };

  const closeMobileSidebar = () => {
    document.body.classList.remove("vertical-sidebar-enable");
  };

  const handleSubLinkClick = () => {
    // Close drawer after selecting a page on mobile/tablet
    closeMobileSidebar();
  };

  return (
    <div className="app-menu navbar-menu xcruit-sidebar">
      <div className="navbar-brand-box">
        <Link to="/dashboard" className="logo xc-brand-logo">
          <span className="logo-sm">
            <img src={allImages.logoIcon} alt="Xcruit" />
          </span>
          <span className="logo-lg">
            <img src={allImages.logoDark} alt="Xcruit Energy" />
          </span>
        </Link>
        <button
          type="button"
          className="xc-sidebar-close"
          onClick={closeMobileSidebar}
          aria-label="Close menu"
        >
          <i className="ri-close-line" aria-hidden="true" />
        </button>
      </div>

      <SimpleBar className="h-100" id="scrollbar">
        <div className="xc-menu-label">
          <span>Menu</span>
        </div>
        <div className="xc-menu-wrap">
          <ul className="navbar-nav" id="navbar-nav">
            {Object.keys(groupedMenus).map((groupName, index) => {
              const groupItems = groupedMenus[groupName];
              const visiblePages = groupItems.filter((page) => page.showInMenu);
              if (!visiblePages.length) return null;

              const collapseId = toCollapseId(groupName);
              const groupActive = isGroupActive(visiblePages);
              const isOpen = openGroup === collapseId;

              return (
                <li
                  className={`nav-item ${groupActive ? "active" : ""}`}
                  key={`${groupName}-${index}`}
                >
                  {visiblePages.length > 1 ? (
                    <>
                      <a
                        className={`nav-link menu-link ${
                          groupActive || isOpen ? "active" : ""
                        }`}
                        href={`#${collapseId}`}
                        role="button"
                        aria-expanded={isOpen ? "true" : "false"}
                        aria-controls={collapseId}
                        title={groupName}
                        onClick={(e) => handleGroupToggle(e, collapseId)}
                      >
                        <div className="xc-menu-icon">
                          <i
                            dangerouslySetInnerHTML={{
                              __html: groupItems[0].groupIcon,
                            }}
                          ></i>
                        </div>
                        <span className="xc-menu-text">{groupName}</span>
                        <i
                          className={`ri-arrow-right-s-line xc-menu-arrow ${
                            isOpen ? "is-open" : ""
                          }`}
                        ></i>
                      </a>
                      <div
                        className={`collapse menu-dropdown ${
                          isOpen ? "show" : ""
                        }`}
                        id={collapseId}
                      >
                        <ul className="nav nav-sm flex-column">
                          {visiblePages.map((page) => {
                            const pagePath = toPagePath(page.pageLink);
                            return (
                              <li
                                className={`nav-item ${
                                  isActive(pagePath) ? "active" : ""
                                }`}
                                key={page.pageId}
                              >
                                <Link
                                  to={pagePath}
                                  className={`nav-link ${
                                    isActive(pagePath) ? "active" : ""
                                  }`}
                                  title={page.pageName}
                                  onClick={handleSubLinkClick}
                                >
                                  <span className="xc-menu-dot" aria-hidden="true"></span>
                                  {page.pageName}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={toPagePath(visiblePages[0].pageLink)}
                      className={`nav-link menu-link ${
                        isActive(toPagePath(visiblePages[0].pageLink))
                          ? "active"
                          : ""
                      }`}
                      title={groupName}
                      onClick={() => {
                        if (
                          isSidebarCollapsed() &&
                          !document.body.classList.contains(
                            "vertical-sidebar-enable",
                          )
                        ) {
                          expandSidebar();
                        }
                        handleSubLinkClick();
                      }}
                    >
                      <div className="xc-menu-icon">
                        <i
                          dangerouslySetInnerHTML={{
                            __html: groupItems[0].groupIcon,
                          }}
                        ></i>
                      </div>
                      <span className="xc-menu-text">{groupName}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </SimpleBar>

      <div className="sidebar-background"></div>
    </div>
  );
};
